// aiGatewayService.js

import { 
  MAIN_URL,
  REQUEST_URL,
  API_CONFIG,
} from "../../constant/ApplicationVariables";
import axios from 'axios';
import { axiosInstance } from "../useAxios/useAxios";
import { fetchCompanyId, fetchCompanyId_API } from "../controller/authController";

// ============================================================
// Token Management - Simplified like Bugzilla service
// ============================================================

/**
 * Get the current auth token - Simplified to match Bugzilla service
 * Using the same token storage approach
 */
const getAuthToken = () => {
  // Get token from localStorage (same as Bugzilla service)
  const token = localStorage.getItem('access-token');
  
  // If not found, try alternative storage locations as fallback
  if (!token) {
    console.warn('Access token not found in localStorage, checking alternatives...');
    return localStorage.getItem('accessToken') || 
           sessionStorage.getItem('access-token') ||
           sessionStorage.getItem('accessToken') ||
           localStorage.getItem('token');
  }
  
  return token;
};

// ============================================================
// Company ID Management - Dynamic fetching
// ============================================================

/**
 * Cache for company ID to avoid repeated API calls
 */
let companyIdCache = null;
let companyIdCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

/**
 * Get company ID dynamically from the API
 * This replaces the static return of 1
 */
export const getCompanyId = async (forceRefresh = false) => {
  // Check cache first
  if (!forceRefresh && companyIdCache && companyIdCacheTime) {
    const elapsed = Date.now() - companyIdCacheTime;
    if (elapsed < CACHE_DURATION) {
      console.log('Using cached company ID:', companyIdCache);
      return companyIdCache;
    }
  }

  try {
    console.log('Fetching company ID dynamically...');
    
    // Use the fetchCompanyId_API
    const response = await fetchCompanyId();
    
    if (response?.status === 200 && response?.data) {
      // Extract company ID from response
      const companyId = response.data.companyId || 
                        response.data.id || 
                        response.data;
      
      if (companyId) {
        // Update cache
        companyIdCache = companyId;
        companyIdCacheTime = Date.now();
        
        // Also store in localStorage for persistence
        localStorage.setItem('company_id', JSON.stringify(companyId));
        
        console.log('Company ID fetched successfully:', companyId);
        return companyId;
      } else {
        throw new Error('Company ID not found in response');
      }
    } else {
      throw new Error('Failed to fetch company ID: Invalid response');
    }
  } catch (error) {
    console.error('Error fetching company ID:', error);
    
    // Try to get from localStorage as fallback
    const storedId = localStorage.getItem('company_id');
    if (storedId) {
      try {
        const parsedId = JSON.parse(storedId);
        console.log('Using stored company ID from localStorage:', parsedId);
        return parsedId;
      } catch (parseError) {
        console.error('Error parsing stored company ID:', parseError);
      }
    }
    
    // If all fails, throw error
    throw new Error('Unable to fetch company ID. Please ensure you are logged in.');
  }
};

/**
 * Clear company ID cache (useful on logout)
 */
export const clearCompanyIdCache = () => {
  companyIdCache = null;
  companyIdCacheTime = null;
  localStorage.removeItem('company_id');
  console.log('Company ID cache cleared');
};

// ============================================================
// Create a dedicated AI Gateway Axios instance
// ============================================================

/**
 * Create a separate axios instance for AI Gateway with consistent interceptors
 * Matching the Bugzilla service pattern
 */
const aiGatewayAxios = axios.create({
  baseURL: MAIN_URL.url,
  timeout: API_CONFIG.timeout || 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Request interceptor - Now handles dynamic company ID
aiGatewayAxios.interceptors.request.use(
  async (config) => {
    // Get token - using same approach as Bugzilla service
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Initialize params if not present
    config.params = config.params || {};

    // Only add company_id if not already present in params
    if (!config.params.company_id && !config.params.companyId) {
      try {
        // Fetch company ID dynamically
        const companyId = await getCompanyId();
        if (companyId) {
          // Use consistent parameter name
          config.params.company_id = companyId;
          console.log(`Added company_id: ${companyId} to request`);
        }
      } catch (error) {
        console.warn('Could not fetch company ID for request:', error.message);
        // Continue without company ID - the API might handle it differently
      }
    }

    console.log(`AI Gateway Request: ${config.method.toUpperCase()} ${config.url}`, {
      headers: config.headers,
      data: config.data,
      params: config.params
    });
    
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor - Modified to NOT automatically logout on 401
aiGatewayAxios.interceptors.response.use(
  (response) => {
    console.log(`AI Gateway Response: ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    // Log the error but don't automatically logout
    if (error.response) {
      const { status, data, config } = error.response;
      
      console.error(`AI Gateway API Error: ${config?.url || 'unknown endpoint'}`, {
        status,
        data,
        method: config?.method?.toUpperCase()
      });
      
      // Check if it's a token-related error
      if (status === 401) {
        console.warn('Authentication error - Token may be expired or invalid');
        
        // Clear company ID cache on auth error
        clearCompanyIdCache();
        
        // Check if the error message indicates invalid token
        const errorMessage = data?.error || data?.message || '';
        const isInvalidToken = errorMessage.toLowerCase().includes('token') || 
                               errorMessage.toLowerCase().includes('jwt') ||
                               errorMessage.toLowerCase().includes('unauthorized');
        
        if (isInvalidToken) {
          console.warn('Invalid token detected. Token will be refreshed on next login.');
          // Don't automatically clear token - let the component handle it
          // The component can check the error and decide what to do
        }
      }
      
      // For other error codes, just log and reject
      if (status === 404) {
        console.error("API endpoint not found:", config?.url);
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        console.error("Cannot connect to AI Gateway service");
      }
    } else {
      console.error("AI Gateway API Error:", error.message);
    }
    
    // Return the error with additional context
    return Promise.reject({
      ...error,
      _isAuthError: error.response?.status === 401,
      _shouldRedirect: false // Don't automatically redirect
    });
  }
);

// ============================================================
// Helper to check if error is authentication related
// ============================================================

export const isAuthError = (error) => {
  return error?.response?.status === 401 || 
         error?._isAuthError === true ||
         error?.response?.data?.error?.toLowerCase().includes('token') ||
         error?.response?.data?.message?.toLowerCase().includes('token') ||
         error?.response?.data?.error?.toLowerCase().includes('unauthorized') ||
         error?.response?.data?.message?.toLowerCase().includes('unauthorized');
};

export const getAuthErrorMessage = (error) => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'Authentication failed. Please try logging in again.';
};

// ============================================================
// AI Gateway Endpoints - Using dedicated axios instance
// ============================================================

/**
 * 1. Get available AI models with credential status
 * GET /admin/ai/models?companyId=...
 */
export const getAIModels_API = async (companyId) => {
  try {
    // If companyId is not provided, fetch it dynamically
    const finalCompanyId = companyId || await getCompanyId();
    
    const response = await aiGatewayAxios({
      url: REQUEST_URL.aiModels,
      method: "GET",
      params: { companyId: finalCompanyId },
    });
    return response;
  } catch (error) {
    console.error('Error fetching AI models:', error);
    return error;
  }
};

/**
 * 2. Generate AI completion
 * POST /admin/ai/completions
 */
export const generateCompletion_API = async (body) => {
  try {
    // If companyId is not in body, fetch it
    if (!body.companyId) {
      try {
        body.companyId = await getCompanyId();
      } catch (error) {
        console.warn('Could not add companyId to body:', error.message);
      }
    }
    
    const response = await aiGatewayAxios({
      url: REQUEST_URL.aiCompletion,
      method: "POST",
      data: body,
    });
    return response;
  } catch (error) {
    console.error('Error generating completion:', error);
    return error;
  }
};

/**
 * 3. Get all credentials for a company
 * GET /admin/ai-credentials?companyId=...
 */
export const getAICredentials_API = async (companyId) => {
  try {
    // If companyId is not provided, fetch it dynamically
    const finalCompanyId = companyId || await getCompanyId();
    
    const response = await aiGatewayAxios({
      url: MAIN_URL.url + REQUEST_URL.aiCredentials,
      method: "GET",
      params: { companyId: finalCompanyId },
    });
    return response;
  } catch (error) {
    console.error('Error fetching credentials:', error);
    return error;
  }
};

/**
 * 4. Save or update AI credentials
 * PUT /admin/ai-credentials
 */
export const saveAICredentials_API = async (body) => {
  console.log(body);
  try {
    // If companyId is not in body, fetch it
    if (!body.companyId) {
      try {
        body.companyId = await getCompanyId();
      } catch (error) {
        console.warn('Could not add companyId to body:', error.message);
      }
    }
    
    const response = await aiGatewayAxios({
      url: MAIN_URL.url + REQUEST_URL.aiCredentials,
      method: "PUT",
      data: body,
    });
    return response;
  } catch (error) {
    console.error('Error saving credentials:', error);
    return error;
  }
};

/**
 * 5. Test AI credentials
 * POST /admin/ai-credentials/test
 */
export const testAICredentials_API = async (body) => {
  try {
    // If companyId is not in body, fetch it
    if (!body.companyId) {
      try {
        body.companyId = await getCompanyId();
      } catch (error) {
        console.warn('Could not add companyId to body:', error.message);
      }
    }
    
    console.log('Testing credentials with body:', body);
    
    const response = await aiGatewayAxios({
      url: REQUEST_URL.aiCredentialsTest,
      method: "POST",
      data: body,
    });
    
    console.log('Test API response:', response);
    console.log('Test API response data:', response.data);
    console.log('Test API response status:', response.status);
    
    // Return the full response with status and data
    return response;
  } catch (error) {
    console.error('Error testing credentials:', error);
    // Return error response with consistent structure
    if (error.response) {
      return error.response;
    }
    return {
      status: 500,
      data: {
        message: error.message || 'Network error occurred'
      }
    };
  }
};

/**
 * 6. Delete AI credentials
 * DELETE /admin/ai-credentials/{provider}?companyId=...
 */
export const deleteAICredentials_API = async (provider, companyId) => {
  try {
    // If companyId is not provided, fetch it dynamically
    const finalCompanyId = companyId || await getCompanyId();
    
    const response = await aiGatewayAxios({
      url: `${REQUEST_URL.aiCredentials}/${provider}`,
      method: "DELETE",
      params: { companyId: finalCompanyId },
    });
    return response;
  } catch (error) {
    console.error('Error deleting credentials:', error);
    return error;
  }
};

/**
 * 7. Validate AI API key without saving
 * POST /admin/ai-credentials/validate
 */
export const validateAICredentials_API = async (body) => {
  try {
    // If companyId is not in body, fetch it
    if (!body.companyId) {
      try {
        body.companyId = await getCompanyId();
      } catch (error) {
        console.warn('Could not add companyId to body:', error.message);
      }
    }
    
    const response = await aiGatewayAxios({
      url: REQUEST_URL.aiCredentialsValidate,
      method: "POST",
      data: body,
    });
    return response;
  } catch (error) {
    console.error('Error validating credentials:', error);
    return error;
  }
};

// ============================================================
// Response Helpers
// ============================================================

export const isSuccess = (response) => {
  return response?.status === 200 || response?.status === 201;
};

export const isError = (response) => {
  return !isSuccess(response);
};

export const getResponseData = (response) => {
  return response?.data || response;
};

export const getErrorMessage = (error) => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred';
};

export const getStatusCode = (response) => {
  return response?.status || response?.statusCode || 500;
};

export const hasData = (response) => {
  return response?.data && Object.keys(response.data).length > 0;
};

export const handleApiError = (error) => {
  const defaultMessage = 'Something went wrong. Please try again.';
  
  if (error?.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return data?.error || data?.message || 'Bad request. Please check your input.';
      case 401:
        // Return a user-friendly message but don't auto-logout
        return data?.error || data?.message || 'Authentication failed. Please check your credentials.';
      case 403:
        return 'You don\'t have permission to perform this action.';
      case 404:
        return 'Resource not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return data?.error || data?.message || defaultMessage;
    }
  }
  
  if (error?.request) {
    return 'Network error. Please check your connection.';
  }
  
  return error?.message || defaultMessage;
};

// ============================================================
// Convenience Functions
// ============================================================

export const generateUserStories = async (companyId, requirement) => {
  const finalCompanyId = companyId || await getCompanyId();
  return generateCompletion_API({
    companyId: finalCompanyId,
    model: "claude-sonnet-4",
    prompt: `Generate User Stories for: ${requirement}`
  });
};

export const generateUseCases = async (companyId, requirement) => {
  const finalCompanyId = companyId || await getCompanyId();
  return generateCompletion_API({
    companyId: finalCompanyId,
    model: "claude-sonnet-4",
    prompt: `Generate Use Cases for: ${requirement}`
  });
};

export const generateTestCases = async (companyId, requirement) => {
  const finalCompanyId = companyId || await getCompanyId();
  return generateCompletion_API({
    companyId: finalCompanyId,
    model: "claude-sonnet-4",
    prompt: `Generate Test Cases for: ${requirement}`
  });
};

export const generateFolders = async (companyId, requirement) => {
  const finalCompanyId = companyId || await getCompanyId();
  return generateCompletion_API({
    companyId: finalCompanyId,
    model: "claude-sonnet-4",
    prompt: `Generate Folders for: ${requirement}`
  });
};

// ============================================================
// Export all functions
// ============================================================

export default {
  // Company ID management
  getCompanyId,
  clearCompanyIdCache,
  
  // AI Gateway endpoints
  getAIModels_API,
  generateCompletion_API,
  getAICredentials_API,
  saveAICredentials_API,
  testAICredentials_API,
  deleteAICredentials_API,
  validateAICredentials_API,
  
  // Convenience functions
  generateUserStories,
  generateUseCases,
  generateTestCases,
  generateFolders,
  
  // Response helpers
  isSuccess,
  isError,
  getResponseData,
  getErrorMessage,
  getStatusCode,
  hasData,
  handleApiError,
  isAuthError,
  getAuthErrorMessage,
  
  // Token helpers
  getAuthToken,
};